import { useState } from 'preact/hooks';
import type { AskUserQuestionData } from '@voice-assistant/shared';

interface Props {
  questions: AskUserQuestionData[];
  onAnswer: (answer: string) => void;
}

/**
 * Interactive card rendered when Claude fires AskUserQuestion via the Agent SDK.
 * Chip buttons map to each option; tapping selects and auto-submits for single-select,
 * or toggles for multiSelect. Touch targets meet R-013 (48dp minimum).
 *
 * Multi-question behaviour (#1818): the first-tap auto-submit applies ONLY to a card with
 * exactly ONE single-select question (the snappy common case). Any card with 2+ questions
 * — regardless of select modes — renders the Submit button and collects all answers
 * together. Single-select chips use radio semantics (tapping another option moves the
 * selection) until submit; rows are never hard-locked pre-submit. The prior behaviour
 * (auto-submit whenever no multiSelect question existed) submitted question 1's answer
 * alone and disabled every other question on the card — live-repro'd on prod 2026-07-10.
 *
 * Free-text "Other" (#1644): the AskUserQuestion tool always offers an implicit "Other"
 * answer the model didn't enumerate. Each question exposes an "Other…" affordance that
 * reveals a labelled text input. When any free-text input is revealed (the reveal IS the
 * intent signal — race-free vs blur) OR carries non-empty text, the first-single-select
 * auto-submit is suppressed and the user must press Submit explicitly. The typed value
 * rides the existing AGENT_QUESTION_ANSWER prose-resume path verbatim — no wire change.
 */
export function AgentQuestionCard({ questions, onAnswer }: Props) {
  const [selectedMap, setSelectedMap] = useState<Record<number, Set<number>>>(() => ({}));
  const [freeText, setFreeText] = useState<Record<number, string>>(() => ({}));
  const [revealed, setRevealed] = useState<Record<number, boolean>>(() => ({}));
  const [submitted, setSubmitted] = useState(false);

  const hasMultiSelect = questions.some(q => q.multiSelect);
  // Auto-submit is safe ONLY when there is nothing else on the card the user could
  // still answer — i.e. exactly one question (#1818). With 2+ questions, the first
  // tap must never end the card.
  const isSingleQuestion = questions.length === 1;
  // Single gate used by BOTH the auto-submit branch and the Submit-button render so they
  // never drift. Reveal-based (not focus-based) → no blur-before-chip-click race.
  const freeTextEngaged =
    Object.values(revealed).some(Boolean) ||
    Object.values(freeText).some(v => v.trim().length > 0);

  function toggleSelected(qIdx: number, oIdx: number, multiSelect: boolean) {
    setSelectedMap((prev) => {
      const next = { ...prev };
      const current = new Set(prev[qIdx] ?? []);
      if (multiSelect) {
        if (current.has(oIdx)) {
          current.delete(oIdx);
        } else {
          current.add(oIdx);
        }
        next[qIdx] = current;
      } else {
        next[qIdx] = new Set([oIdx]);
      }
      return next;
    });
  }

  function buildPart(qi: number): string | null {
    const q = questions[qi];
    const sel = selectedMap[qi];
    const labels = sel ? [...sel].map((oi) => q.options[oi]?.label).filter(Boolean) : [];
    const typed = (freeText[qi] ?? '').trim();
    if (labels.length === 0 && !typed) return null;
    const values = typed ? [...labels, typed] : labels;
    const header = q.header ? `[${q.header}]` : '';
    const joined = values.join(', ');
    return header ? `${header}: ${joined}` : joined;
  }

  function handleSubmit() {
    if (submitted) return;
    const parts: string[] = [];
    for (let qi = 0; qi < questions.length; qi++) {
      const part = buildPart(qi);
      if (part) parts.push(part);
    }
    if (parts.length === 0) return;
    setSubmitted(true);
    onAnswer(parts.join('\n'));
  }

  function handleChipClick(qIdx: number, oIdx: number, multiSelect: boolean) {
    toggleSelected(qIdx, oIdx, multiSelect);
    if (isSingleQuestion && !multiSelect && !hasMultiSelect && !freeTextEngaged) {
      // Exactly ONE single-select question, no free text in play — auto-submit (#1818:
      // multi-question cards must NEVER auto-submit; the first tap used to end the card
      // with only question 1 answered and every other row disabled).
      const q = questions[qIdx];
      const label = q.options[oIdx]?.label;
      if (!label) return;
      setSubmitted(true);
      const header = q.header ? `[${q.header}]` : '';
      onAnswer(header ? `${header}: ${label}` : label);
    }
    // Multi-question card OR multiSelect OR free text engaged: selection stays editable
    // (radio semantics per row) and the explicit Submit collects everything.
  }

  function revealFreeText(qIdx: number) {
    setRevealed((prev) => ({ ...prev, [qIdx]: true }));
  }

  const showSubmit = (!isSingleQuestion || hasMultiSelect || freeTextEngaged) && !submitted;

  return (
    <div class={`aqn-card${submitted ? ' aqn-card--submitted' : ''}`}>
      {questions.map((q, qi) => {
        const isRevealed = !!revealed[qi];
        const inputId = `aqn-other-${qi}`;
        return (
          <div key={qi} class="aqn-question">
            {q.header && (
              <div class="aqn-header">
                <span class="aqn-header-chip">{q.header}</span>
              </div>
            )}
            <p class="aqn-question-text">{q.question}</p>
            <div class="aqn-chips" role="group" aria-label={q.question}>
              {q.options.map((opt, oi) => {
                const isSelected = selectedMap[qi]?.has(oi) ?? false;
                return (
                  <button
                    key={oi}
                    type="button"
                    class={`aqn-chip${isSelected ? ' aqn-chip--selected' : ''}`}
                    onClick={() => handleChipClick(qi, oi, !!q.multiSelect)}
                    disabled={submitted}
                    title={opt.description}
                    aria-pressed={isSelected}
                    style={{ minHeight: '48px' }}
                  >
                    {opt.label}
                  </button>
                );
              })}
              {!isRevealed && !submitted && (
                <button
                  type="button"
                  class="aqn-chip aqn-chip--other"
                  onClick={() => revealFreeText(qi)}
                  aria-expanded={false}
                  aria-controls={inputId}
                  style={{ minHeight: '48px' }}
                >
                  Other…
                </button>
              )}
            </div>
            {isRevealed && (
              <div class="aqn-other-field">
                <label class="aqn-other-label" htmlFor={inputId}>
                  Your answer
                </label>
                <input
                  id={inputId}
                  type="text"
                  class="aqn-other-input"
                  value={freeText[qi] ?? ''}
                  placeholder="Type a custom answer…"
                  disabled={submitted}
                  aria-label={`Custom answer for: ${q.question}`}
                  onInput={(e) =>
                    setFreeText((prev) => ({ ...prev, [qi]: (e.target as HTMLInputElement).value }))
                  }
                />
              </div>
            )}
          </div>
        );
      })}
      {showSubmit && (
        <button
          type="button"
          class="aqn-submit"
          onClick={handleSubmit}
          style={{ minHeight: '48px' }}
        >
          Submit
        </button>
      )}
    </div>
  );
}
